import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Menu, X, ArrowRight } from 'lucide-react';

const BLOG_URL = '/blog/guia-completa-ventas-consultivas-b2b-2026';
const NUDGE_KEY = 'sim_b2b_nudge_dismissed';
import SEOHead from '../components/SEOHead';
import PublicTrialSimulator from '../publicTrial/PublicTrialSimulator';

const SimuladorB2B = () => {
  const [infoOpen, setInfoOpen] = useState(false);
  const [nudgeVisible, setNudgeVisible] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(NUDGE_KEY)) setNudgeVisible(true);
  }, []);

  const dismissNudge = () => {
    localStorage.setItem(NUDGE_KEY, '1');
    setNudgeVisible(false);
  };

  return (
    // Shell de app: ocupa toda la pantalla, sin scroll exterior — igual que VertexChatWindow
    <div className="fixed inset-0 flex flex-col bg-white">
      <SEOHead
        title="Simulador B2B — Practica negociación en vivo | Edutech Pulse Lab"
        description="Practica una negociación B2B real con IA. Sin registro. Dos turnos para mostrar cómo gestionas decisiones complejas."
        canonicalUrl="/simulador-b2b"
        ogType="website"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Simulador B2B — Negociación con IA",
          "description": "Simulador interactivo de negociación B2B con inteligencia artificial. Practica cómo destrabar decisiones complejas con un comité comprador real.",
          "url": "https://edutechpulselab.com/simulador-b2b",
          "applicationCategory": "EducationalApplication",
          "operatingSystem": "Web",
          "isAccessibleForFree": true,
          "inLanguage": "es",
          "provider": {
            "@type": "Organization",
            "name": "Edutech Pulse Lab",
            "url": "https://edutechpulselab.com"
          },
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "about": {
            "@type": "Thing",
            "name": "Negociación B2B",
            "description": "Técnicas de ventas consultivas y negociación estratégica en entornos B2B"
          }
        }}
      />

      {/* ── App bar compacto (reemplaza Navbar + encabezado de página) ─── */}
      <div
        className="bg-[#081827] flex items-center gap-3 px-4 flex-shrink-0"
        style={{ height: '52px' }}
      >
        <Link
          to={BLOG_URL}
          className="flex items-center gap-1.5 -ml-1 px-1.5 py-1 text-gray-400 hover:text-white transition-colors rounded group"
          aria-label="Volver al artículo"
        >
          <ArrowLeft className="w-5 h-5 flex-shrink-0" />
          <span className="hidden sm:inline text-[12px] font-medium text-gray-400 group-hover:text-white leading-none">
            Guía B2B
          </span>
        </Link>
        {/* Mobile: isotipo EPL. La contraparte (Kuro) la gestionará el simulador en su header */}
        <img
          src="/isotipoedutech.png"
          alt="Edutech Pulse Lab"
          className="w-8 h-8 rounded-full flex-shrink-0 ring-1 ring-white/20 lg:hidden"
        />
        {/* Desktop: isotipo EPL */}
        <img
          src="/isotipoedutech.png"
          alt="Edutech Pulse Lab"
          className="hidden lg:block w-8 h-8 rounded-full flex-shrink-0 ring-1 ring-white/20"
        />
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-sm leading-tight truncate">Simulador B2B</p>
          <p className="text-gray-500 text-[11px] leading-tight truncate">Negociación con comité comprador</p>
        </div>
        <button
          onClick={() => setInfoOpen(true)}
          className="p-1.5 -mr-1 text-gray-400 hover:text-white transition-colors"
          aria-label="Más información"
        >
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {/* ── Nudge: leer teoría antes de practicar (solo primera visita) ── */}
      {nudgeVisible && (
        <div className="flex items-center gap-2 px-4 py-2 bg-[#0d2238] border-b border-white/10 flex-shrink-0">
          <Link
            to={BLOG_URL}
            onClick={dismissNudge}
            className="flex-1 text-[12px] text-gray-400 hover:text-gray-200 transition-colors leading-tight"
          >
            ¿Primera vez aquí?{' '}
            <span className="text-amber-400 font-medium underline underline-offset-2">
              Lee la guía de ventas consultivas B2B
            </span>
          </Link>
          <button
            onClick={dismissNudge}
            className="p-0.5 text-gray-600 hover:text-gray-300 transition-colors flex-shrink-0"
            aria-label="Cerrar"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ── Simulador ─────────────────────────────────────────────────── */}
      {/* Mobile: fill remaining height. Desktop: card centrado con max-w  */}
      <div
        className="overflow-hidden lg:bg-[#EEF2F7] lg:flex lg:items-start lg:justify-center lg:p-6"
        style={{ height: nudgeVisible ? 'calc(100dvh - 52px - 36px)' : 'calc(100dvh - 52px)' }}
      >
        <div className="h-full w-full lg:max-w-5xl lg:rounded-xl lg:overflow-hidden lg:shadow-[0_4px_24px_rgba(0,0,0,0.10)] lg:border lg:border-slate-200/70">
          <PublicTrialSimulator trialKey="b2b" />
        </div>
      </div>

      {/* ── Info sheet (hamburger) ───────────────────────────────────── */}
      {infoOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setInfoOpen(false)}
          />
          <div
            className="fixed bottom-0 left-0 right-0 z-50 bg-white flex flex-col"
            style={{
              borderRadius: '16px 16px 0 0',
              maxHeight: '72dvh',
              boxShadow: '0 -4px 24px rgba(0,0,0,0.15)'
            }}
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-9 h-1 rounded-full bg-slate-200" />
            </div>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 flex-shrink-0">
              <div>
                <p className="text-[10px] font-semibold tracking-widest text-slate-400 uppercase">
                  Edutech Pulse Lab
                </p>
                <h2 className="font-bold text-[#0c203b] text-base leading-snug">Simulador B2B</h2>
              </div>
              <button
                onClick={() => setInfoOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-700 rounded-full hover:bg-slate-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* Content */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <p className="text-slate-600 text-sm leading-relaxed">
                Tres turnos. Una conversación difícil. Un feedback para medir tu criterio.
              </p>
              <p className="text-slate-600 text-sm leading-relaxed">
                Practica cómo destrabar una decisión B2B cuando finanzas, tecnología y el sponsor no están alineados.
              </p>
              <p className="text-slate-400 text-xs leading-relaxed">
                Motor IA propietario. Escenarios diseñados para decisiones ejecutivas.
              </p>
              <div className="flex flex-col gap-2">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center gap-2 h-10 px-5 bg-[#1E3A8A] hover:bg-[#17316F] text-white text-[13px] font-semibold rounded-lg transition-colors"
                >
                  Crear cuenta gratuita y seguir entrenando
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center h-9 text-[#0c203b] text-[13px] font-medium hover:underline"
                >
                  Ya tengo cuenta → Acceder
                </Link>
              </div>
              <div className="rounded-lg border border-amber-100 bg-amber-50 p-3">
                <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-wide mb-1">Teoría detrás del simulador</p>
                <Link
                  to={BLOG_URL}
                  onClick={() => setInfoOpen(false)}
                  className="text-[13px] text-amber-800 font-medium hover:underline underline-offset-2 leading-snug block"
                >
                  Guía completa de ventas consultivas B2B 2026 →
                </Link>
              </div>
              <div className="border-t border-slate-100 pt-3 flex gap-5 flex-wrap">
                <Link to="/" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">Inicio</Link>
                <Link to="/blog" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">Blog</Link>
                <Link to="/register" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">Registro</Link>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SimuladorB2B;
